import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	OnDestroy,
	OnInit,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { ButtonToggle } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { Map, Set } from 'immutable';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	of,
	pairwise,
	Subject,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { ResizeProvider } from '../../../core/app-resize-wrapper/resize-provider';
import { configuration } from '../../../core/configuration';
import { FunctionalDocumentationKeys } from '../../../core/documentation/functional-doc-keys';
import { ElementPermission } from '../../../core/domain/permission-types';
import { ProjectStorage } from '../../../core/services/project.storage';
import { goToHdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { ColumnConfigItemEntity } from '../../domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../../domain/column-model-item.entity';
import { FilterModelItemEntity } from '../../domain/filter-model-item.entity';
import { InternalMetadataEntity } from '../../domain/internal-metadata.entity';
import { ProfileItemEntity } from '../../domain/profile-item.entity';
import { PublicMetadataEntity } from '../../domain/public-metadata.entity';
import {
	ConsumeStatus,
	FilterVersion,
	SubscriptionAction,
	SubscriptionStatus,
} from '../../domain/subscription.types';
import { HighlightProvider } from '../../providers/highlight.provider';
import {
	activateSubscription,
	clearSubscriptionData,
	deactivateSubscription,
	deleteBulkConsumedProject,
	deleteConsumedProject,
	deleteConsumedProjectSuccess,
	getConsumedProfileColumns,
	getSwitchableConsumedProjectsForSubscription,
	initPermissionsConfig,
	initSubscriptionView,
	navigateToSubscriptionList,
	navigateToSubscriptionSimulation,
	openDeleteConfirmPopupOnView,
	openInitializeConfirmPopup,
	openUpdateInternalMetadataPopup,
	openUpdatePublicMetadataPopup,
	refreshConsumedProjects,
	refreshSubscriptionColumnModels,
	refreshSubscriptionColumns,
	refreshSubscriptionFilterModels,
	refreshSubscriptionProfiles,
	refreshSubscriptionProfilesWithModels,
	reloadProfilesAfterActionSuccess,
	resetSubscriptionView,
	saveNewConsumers,
	saveNewConsumersSuccess,
	SubscriptionSelector,
	switchVersionForConsumedProjectsOnViewSuccess,
	toggleBulkProjectConsume,
	toggleProjectConsume,
	toggleProjectConsumeSuccess,
	updateConsumer,
	updateConsumerSuccess,
	updateMembersAccess,
	updateSubscriptionColumns,
	updateSubscriptionData,
} from '../../store';
import {
	ISubscriptionConfirmationModalData,
	SubscriptionActionConfirmPopupComponent,
	SubscriptionActionEnum,
} from '../subscription-action-confirm-popup/subscription-action-confirm-popup.component';
import { VersionSwitcherModalComponent } from '../version-switcher-modal/version-switcher-modal.component';
import {
	ActionConfirmPopupComponent,
	IActionConfirmationModalData,
} from './action-confirm-popup/action-confirm-popup.component';
import { ColumnsConfigModalComponent } from './columns-config-modal/columns-config-modal.component';
import { ConsumedProjectItemEntity } from './consumed-project-item.entity';
import { ConsumedProjectListEntity } from './consumed-project-list.entity';
import {
	ConsumerConfigAction,
	ConsumerConfigModalComponent,
} from './consumer-config-modal/consumer-config-modal.component';
import { HistoryListModalComponent } from './history-list-modal/history-list-modal.component';
import { PermissionsConfigModalComponent } from './permissions-config-modal/permissions-config-modal.component';
import { ConsumedProjectListProvider } from './providers/consumed-project-list.provider';
import { SubscriptionDetailsProvider } from './providers/subscription-details.provider';
import { PublishedSubscriptionEntity } from './published-subscription.entity';

type SubscriptionOverlayConsumer = {
	consumerId: number | undefined;
};

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<ConsumedProjectItemEntity>) => void;
};

@Component({
	selector: 'app-subscription-view',
	templateUrl: './subscription-view.component.html',
	styleUrls: ['./subscription-view.component.scss'],
	inputs: ['subscriptionId', 'projectLabel'],
	providers: [
		SubscriptionDetailsProvider,
		ConsumedProjectListProvider,
		HighlightProvider,
	],
})
export class SubscriptionViewComponent
	extends DcBaseComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly FunctionalDocumentationKeys = FunctionalDocumentationKeys;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ActionIcons = ActionIcons;
	protected readonly SubscriptionStatus = SubscriptionStatus;
	protected readonly ConsumeStatus = ConsumeStatus;
	protected readonly ElementPermission = ElementPermission;
	protected readonly Configuration = configuration;
	protected readonly FilterVersion = FilterVersion;

	public paths: Array<IBreadCrumbEntry> = [];
	public currentlyHovered = -1;
	public selectionCount = 0;
	public toggleActions = false;

	public columnsActions: Array<PanelAction> = [];
	public consumerActions: Array<PanelAction> = [];
	public activateConsumeActions: Array<RowAction> = [];
	public deactivateConsumeActions: Array<RowAction> = [];
	public syncHeaderFilter = [
		{
			text: $localize`:i18n=@@general.status.not-synced:`,
			value: false,
		},
		{
			text: $localize`:i18n=@@general.status.synced:`,
			value: true,
		},
	];

	public consumeStatusHeaderFilter = [
		{
			text: $localize`:i18n=@@general.status.notUsed:`,
			value: ConsumeStatus.NotUsed,
		},
		{
			text: $localize`:i18n=@@general.status.paused:`,
			value: ConsumeStatus.Paused,
		},
		{
			text: $localize`:i18n=@@general.status.absent:`,
			value: ConsumeStatus.Absent,
		},
		{
			text: $localize`:i18n=@@general.status.inProgress:`,
			value: ConsumeStatus.InProgress,
		},
	];

	public filterVersionOptions: Array<ButtonToggle> = [
		{
			id: 1,
			label: $localize`:i18n=@@subscription.consumedProject.queryFilter.activeVersion:`,
			isSelected: true,
			cb: (): void =>
				this.currentFilterVersion$.next(FilterVersion.CurrentVersion),
		},
		{
			id: 2,
			label: $localize`:i18n=@@subscription.consumedProject.queryFilter.inactiveVersions:`,
			isSelected: false,
			cb: (): void =>
				this.currentFilterVersion$.next(FilterVersion.OtherVersions),
		},
		{
			id: 3,
			label: $localize`:i18n=@@subscription.consumedProject.queryFilter.allVersions:`,
			isSelected: false,
			cb: (): void =>
				this.currentFilterVersion$.next(FilterVersion.AllVersions),
		},
	];

	public vo$: Observable<{
		published: PublishedSubscriptionEntity | null;
		internalMetadata: InternalMetadataEntity;
		publicMetadata: PublicMetadataEntity;
		userPermissions: Set<ElementPermission>;
		projectVersionCount: number;
		actionInProgress: SubscriptionAction;
		columns: Map<string, ColumnConfigItemEntity>;
		profiles: Map<string, ProfileItemEntity>;
		columnModels: Map<string, ColumnModelItemEntity>;
		filterModels: Map<string, FilterModelItemEntity>;
		consumedProjectList: ConsumedProjectListEntity;
		openedOverlayConsumer: SubscriptionOverlayConsumer;
		currentFilterVersion: FilterVersion;
	}>;

	@ViewChild(DxDataGridComponent)
	public grid: DxDataGridComponent | null = null;

	private readonly showPublishedInfo = new BehaviorSubject<boolean>(false);

	private readonly openedOverlayConsumer$ =
		new BehaviorSubject<SubscriptionOverlayConsumer>({
			consumerId: undefined,
		});
	private readonly isGridReadySubject$ = new Subject<boolean>();
	private readonly currentFilterVersion$ = new BehaviorSubject<FilterVersion>(
		FilterVersion.CurrentVersion
	);

	public constructor(
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly selector: SubscriptionSelector,
		private readonly renderer: Renderer2,
		private readonly elemRef: ElementRef,
		private readonly subscriptionDetailsProvider: SubscriptionDetailsProvider,
		private readonly consumedProjectListProvider: ConsumedProjectListProvider,
		private readonly resizeProvider: ResizeProvider,
		private readonly highlightProvider: HighlightProvider,
		private readonly action$: Actions,
		private readonly projectStorage: ProjectStorage,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'subscription-view';

		const consumedProjectList$ = this.selector
			.getConsumedProjects$()
			.pipe(
				map((items) => this.consumedProjectListProvider.setListElements(items))
			);

		const columnsForSubscription$ = this.selector
			.getSubscriptionColumns$()
			.pipe(map((columns) => columns.filter((col) => col.isSelectedInSource)));

		const published$ = combineLatest([
			this.showPublishedInfo,
			this.selector.getPublishedSubscription$(),
		]).pipe(
			map(([showPublishedInfo, published]) =>
				showPublishedInfo ? published : null
			)
		);

		this.vo$ = combineAll({
			published: published$,
			actionInProgress: this.selector.getActionInProgress$(),
			internalMetadata: this.selector.getInternalMetadata$(),
			publicMetadata: this.selector.getPublicMetadata$(),
			userPermissions: this.selector.getSubscriptionUserPermissions$(),
			projectVersionCount: this.projectStorage.getProjectVersionCount(),
			columns: columnsForSubscription$,
			profiles: this.selector.getSubscriptionProfiles$(),
			columnModels: this.selector.getSubscriptionColumnModels$(),
			filterModels: this.selector.getSubscriptionFilterModels$(),
			consumedProjectList: consumedProjectList$,
			openedOverlayConsumer: this.openedOverlayConsumer$,
			currentFilterVersion: this.currentFilterVersion$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.store.dispatch(resetSubscriptionView());
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		setTimeout(() => {
			this.subscriptionDetailsProvider.adjustHeights(
				this.elemRef,
				this.renderer
			);
			this.showPublishedInfo.next(true);
		}, 960);
	}

	public async navigateToSubscriptionSimulator(): Promise<void> {
		const subscriptionId = await firstValueFrom(
			this.toObservable<number>('subscriptionId')
		);

		this.store.dispatch(
			navigateToSubscriptionSimulation({
				subscriptionId,
			})
		);
	}

	public openSwitchableConsumedProjectsModal(): void {
		this.store.dispatch(getSwitchableConsumedProjectsForSubscription());

		this.modalService.openModal<{ inViewMode: boolean }>(
			VersionSwitcherModalComponent,
			{
				title: $localize`:i18n=@@subscription.versionSwitcher.modal.header:`,
				subTitle: '',
				inViewMode: true,
			},
			{
				disableClose: true,
			}
		);
	}

	public updateInternalMetadata(): void {
		this.store.dispatch(openUpdateInternalMetadataPopup());
	}

	public updatePublicMetadata(): void {
		this.store.dispatch(openUpdatePublicMetadataPopup());
	}

	public openDeleteConfirmationPopup(): void {
		this.store.dispatch(openDeleteConfirmPopupOnView());
	}

	public async openPermissionsConfigModal(): Promise<void> {
		this.store.dispatch(initPermissionsConfig());
		const modalRef = this.modalService.openModal(
			PermissionsConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.permissions.update.header:`,
				subTitle: '',
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const { membersAccess } = modalResult;

		this.store.dispatch(
			updateMembersAccess({
				membersAccess,
			})
		);
	}

	public async openUpdateColumnsModal(): Promise<void> {
		const modalRef = this.modalService.openModal(
			ColumnsConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.columns.modal.title:`,
				subTitle: '',
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const { columns } = modalResult;

		this.store.dispatch(
			updateSubscriptionColumns({
				columns,
			})
		);
	}

	public async refreshConsumedProjects(): Promise<void> {
		const filterVersion = await firstValueFrom(this.currentFilterVersion$);
		this.store.dispatch(
			refreshConsumedProjects({
				filterVersion,
			})
		);
	}

	public refreshColumnList(): void {
		this.store.dispatch(refreshSubscriptionColumns());
	}

	public refreshProfiles(): void {
		this.store.dispatch(refreshSubscriptionProfilesWithModels());
	}

	public refreshColumnModels(): void {
		this.store.dispatch(refreshSubscriptionColumnModels());
	}

	public refreshFilterModels(): void {
		this.store.dispatch(refreshSubscriptionFilterModels());
	}

	public async openPublishConfirmPopup(): Promise<void> {
		const published = await firstValueFrom(
			this.selector.getPublishedSubscription$()
		);

		if (published.status === SubscriptionStatus.Waiting) {
			return;
		}

		this.store.dispatch(openInitializeConfirmPopup());
	}

	public async openUpdateDataConfirmPopup(): Promise<void> {
		const subscriptionId = await firstValueFrom(
			this.toObservable<number>('subscriptionId')
		);

		const publicMetadata = await firstValueFrom(
			this.selector.getPublicMetadata$()
		);
		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.updateData.header:`,
					subTitle: '',
					subscriptionId,
					element: publicMetadata.title,
					action: SubscriptionActionEnum.UpdateData,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		this.store.dispatch(
			updateSubscriptionData({
				subscriptionId,
				operationType: 'update-data',
			})
		);
	}

	public async openDeactivateConfirmPopup(): Promise<void> {
		const subscriptionId = await firstValueFrom(
			this.toObservable<number>('subscriptionId')
		);

		const publicMetadata = await firstValueFrom(
			this.selector.getPublicMetadata$()
		);
		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.deactivate.header:`,
					subTitle: '',
					subscriptionId,
					element: publicMetadata.title,
					action: SubscriptionActionEnum.Deactivate,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		this.store.dispatch(
			deactivateSubscription({
				subscriptionId,
				inViewMode: true,
			})
		);
	}

	public async openActivateConfirmPopup(): Promise<void> {
		const subscriptionId = await firstValueFrom(
			this.toObservable<number>('subscriptionId')
		);

		const publicMetadata = await firstValueFrom(
			this.selector.getPublicMetadata$()
		);
		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.activate.header:`,
					subTitle: '',
					subscriptionId,
					element: publicMetadata.title,
					action: SubscriptionActionEnum.Activate,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		this.store.dispatch(
			activateSubscription({
				subscriptionId,
				inViewMode: true,
			})
		);
	}

	public async openClearConfirmPopup(): Promise<void> {
		const subscriptionId = await firstValueFrom(
			this.toObservable<number>('subscriptionId')
		);

		const publicMetadata = await firstValueFrom(
			this.selector.getPublicMetadata$()
		);
		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.clearData.header:`,
					subTitle: '',
					subscriptionId,
					element: publicMetadata.title,
					action: SubscriptionActionEnum.ClearData,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		this.store.dispatch(
			clearSubscriptionData({
				subscriptionId,
				inViewMode: true,
			})
		);
	}

	public openHistoryListModal(): void {
		this.modalService.openModal(
			HistoryListModalComponent,
			{
				title: $localize`:i18n=@@subscription.history.list.modal.header:`,
				subTitle: '',
			},
			{
				disableClose: true,
			}
		);
	}

	public async updateConsumedProject(
		item: ConsumedProjectItemEntity
	): Promise<void> {
		const modalRef = this.modalService.openModal<{
			consumerConfig: ConsumedProjectItemEntity;
			action: ConsumerConfigAction;
		}>(
			ConsumerConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.consumer.update.title:`,
				subTitle: '',
				consumerConfig: item,
				action: ConsumerConfigAction.Update,
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const { profileUuid } = modalResult;

		this.store.dispatch(
			updateConsumer({
				profileUuid,
				projectUuid: item.projectUuid,
			})
		);
	}

	public async deleteConsumedProject(
		selectedItem: ConsumedProjectItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}
			const selectedRows = (
				this.grid.instance.getSelectedRowsData() as Array<ConsumedProjectItemEntity>
			).filter((s) => s.isInCurrentVersion);

			if (selectedRows.length === 0) {
				return;
			}

			selected = selectedRows.map((r) => r.id);
			elementLabel =
				selectedRows.length === 1 ? selectedRows[0].projectLabel : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.projectLabel;
		}

		const modalRef = this.modalService.openPopup<IActionConfirmationModalData>(
			ActionConfirmPopupComponent,
			{
				title: $localize`:i18n=@@subscription.consumedProject.action.confirmation.popup.delete.header:`,
				subTitle: '',
				id: selected.length === 1 ? selected[0] : selected,
				element: elementLabel,
				action: 'DELETE_CONSUMER',
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}

		if (selected.length === 1) {
			return this.store.dispatch(
				deleteConsumedProject({
					consumedProjectId: selected[0],
				})
			);
		}

		this.store.dispatch(
			deleteBulkConsumedProject({
				consumedProjectIds: selected,
			})
		);
	}

	public async updateProjectConsumeStatus(
		isActive: boolean,
		selectedItem: ConsumedProjectItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}

			const expectedStatus = isActive
				? ConsumeStatus.Paused
				: ConsumeStatus.InProgress;

			const selectedRows = (
				this.grid.instance.getSelectedRowsData() as Array<ConsumedProjectItemEntity>
			).filter((s) => s.status === expectedStatus && s.isInCurrentVersion);

			if (selectedRows.length === 0) {
				return;
			}

			selected = selectedRows.map((r) => r.id);
			elementLabel =
				selectedRows.length === 1 ? selectedRows[0].projectLabel : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.projectLabel;
		}

		const modalRef = this.modalService.openPopup<IActionConfirmationModalData>(
			ActionConfirmPopupComponent,
			{
				title: isActive
					? $localize`:i18n=@@subscription.consumedProject.action.confirmation.popup.activate.header:`
					: $localize`:i18n=@@subscription.consumedProject.action.confirmation.popup.deactivate.header:`,
				subTitle: '',
				id: selected.length === 1 ? selected[0] : selected,
				element: elementLabel,
				action: isActive ? 'ACTIVATE_CONSUMER' : 'DEACTIVATE_CONSUMER',
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}

		if (selected.length === 1) {
			return this.store.dispatch(
				toggleProjectConsume({
					id: selected[0],
					isActive,
				})
			);
		}

		this.store.dispatch(
			toggleBulkProjectConsume({
				ids: selected,
				isActive,
			})
		);
	}

	public async openColumnsPopover(
		event: Event,
		consumerId: number
	): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlayConsumer$);

		if (previous.consumerId === consumerId) {
			this.openedOverlayConsumer$.next({
				...previous,
				consumerId: undefined,
			});
			return;
		}

		this.openedOverlayConsumer$.next({
			...previous,
			consumerId,
		});

		this.store.dispatch(
			getConsumedProfileColumns({
				consumerId,
			})
		);
	}

	public closeColumnsPopover(): void {
		this.openedOverlayConsumer$.next({
			consumerId: undefined,
		});
	}

	public onTabChange(): void {
		setTimeout(
			() =>
				this.subscriptionDetailsProvider.adjustHeights(
					this.elemRef,
					this.renderer
				),
			960
		);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onSelectionChange(event: SelectionChangedEvent): void {
		this.toggleActions = event.selectedRowsData.length > 0;
		this.selectionCount = event.selectedRowsData.length;
	}

	public onGridReady(): void {
		this.isGridReadySubject$.next(true);
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	private async openAddConsumerModal(): Promise<void> {
		const modalRef = this.modalService.openModal<{
			consumerConfig: ConsumedProjectItemEntity;
			action: ConsumerConfigAction;
		}>(
			ConsumerConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.consumer.create.title:`,
				subTitle: $localize`:i18n=@@subscription.consumer.create.subTitle:`,
				consumerConfig: ConsumedProjectItemEntity.build(),
				action: ConsumerConfigAction.Create,
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const { profileUuid, consumerIds } = modalResult;

		return this.store.dispatch(
			saveNewConsumers({
				profileUuid,
				consumerIds,
			})
		);
	}

	private handleSubscription(): void {
		this.resizeProvider.data$
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() =>
					this.subscriptionDetailsProvider.adjustHeights(
						this.elemRef,
						this.renderer
					)
				)
			)
			.subscribe();

		this.action$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(
					saveNewConsumersSuccess,
					updateConsumerSuccess,
					deleteConsumedProjectSuccess,
					toggleProjectConsumeSuccess,
					switchVersionForConsumedProjectsOnViewSuccess,
					refreshSubscriptionProfiles,
					reloadProfilesAfterActionSuccess
				),
				withLatestFrom(this.currentFilterVersion$),
				tap(([, currentFilterVersion]) => {
					this.store.dispatch(
						refreshConsumedProjects({
							filterVersion: currentFilterVersion,
						})
					);
				})
			)
			.subscribe();

		this.toObservable<number>('subscriptionId')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((subscriptionId) => {
					this.store.dispatch(
						initSubscriptionView({
							subscriptionId,
						})
					);
				})
			)
			.subscribe();

		this.currentFilterVersion$
			.pipe(
				pairwise(),
				takeUntil(this.onDestroy$),
				tap(([, filterVersion]) => {
					this.store.dispatch(
						refreshConsumedProjects({
							filterVersion,
						})
					);
				})
			)
			.subscribe();

		combineAll({
			metadata: this.selector.getInternalMetadata$(),
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ metadata, projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToHdHome()),
						},
						{
							label: $localize`:i18n=@@subscription.breadcrumb.list:`,
							isActive: true,
							tooltip: $localize`:i18n=@@subscription.breadcrumb.list.tooltip:`,
							callback: (): void =>
								this.store.dispatch(navigateToSubscriptionList()),
						},
						{
							label: metadata.label,
							isActive: false,
							shouldTruncate: true,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		this.isGridReadySubject$
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(300),
				tap(() => {
					this.grid?.instance.updateDimensions();
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.Add,
			ActionIcons.Update,
			ActionIcons.Delete,
			ActionIcons.ActivateElement,
			ActionIcons.DeactivateElement,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		this.columnsActions = [
			{
				order: 0,
				command: (): Promise<void> => this.openUpdateColumnsModal(),
				iconName: actionMap.get(ActionIcons.Update)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@general.action.update:`,
			},
		];

		this.consumerActions = [
			{
				order: 0,
				command: (): Promise<void> => this.openAddConsumerModal(),
				iconName: actionMap.get(ActionIcons.Add)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@general.action.new:`,
			},
			{
				order: 1,
				command: (): Promise<void> => this.updateProjectConsumeStatus(true),
				iconName: actionMap.get(ActionIcons.ActivateElement)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@subscription.consumedProject.list.action.activate:`,
				typed: 'plain-context-action',
			},
			{
				order: 2,
				command: (): Promise<void> => this.updateProjectConsumeStatus(false),
				iconName: actionMap.get(ActionIcons.DeactivateElement)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@subscription.consumedProject.list.action.deactivate:`,
				typed: 'plain-context-action',
			},
			{
				order: 3,
				command: (): Promise<void> => this.deleteConsumedProject(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@subscription.consumedProject.list.action.delete:`,
				typed: 'plain-context-action',
			},
		];

		this.activateConsumeActions = [
			{
				actionName: 'resume',
				description: $localize`:i18n=@@subscription.consumedProject.list.action.activate:`,
				action: (item): Promise<void> =>
					this.updateProjectConsumeStatus(true, item),
				icon: DcIcons.Flash,
			},
		];

		this.deactivateConsumeActions = [
			{
				actionName: 'pause',
				description: $localize`:i18n=@@subscription.consumedProject.list.action.deactivate:`,
				action: (item): Promise<void> =>
					this.updateProjectConsumeStatus(false, item),
				icon: DcIcons.FlashOff,
			},
		];
	}
}
